import { useEffect, useRef, useState } from 'react';

import { Box } from '@mui/system';
import dayjs from 'dayjs';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';

import { IconButton, Stack, Typography } from '@mui/material';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ThermostatIcon from '@mui/icons-material/Thermostat';

import { store, toggleShowGps } from '../../store';
import { setHighlightedGpsDataFilteredIndex } from '../../store/gpsData';
import { setHighlightedSensorDataFiltered } from '../../store/sensorData';
import { computeMode } from '../../utils/data';

interface Props {
  mapRef: any;
  height?: number;
}

export default ({ mapRef, height }: Props) => {
  const { t } = useTranslation();
  const snap = useSnapshot(store);
  const { showGps } = snap.layout;
  const { filtered: gpsDataFiltered } = snap.gpsData;
  const { data } = snap.linechart;
  const { filtered: powerOutagesFiltered } = snap.powerOutages;
  const { filtered: temperatureBreachesFiltered } = snap.temperatureBreaches;
  const [powerOutagesIndex, setPowerOutagesIndex] = useState<number[]>([]);
  const [setpointMode, setSetpointMode] = useState<number | null>(null);
  const [temperatureBreachesIndex, setTemperatureBreachesIndex] = useState<
    number[]
  >([]);
  const zoomPoint = useRef<{
    latitude: number;
    longitude: number;
  } | null>(null);

  const ref = useRef<ReactECharts>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // useEffect(() => {
  //   const powerOutagesIndex: number[] = [];
  //   for (let i = 0; i < data.length; i++) {
  //     if (data[i].powerOutage) {
  //       powerOutagesIndex.push(i);
  //     }
  //   }
  //   setPowerOutagesIndex(powerOutagesIndex);

  //   const temperatureBreachesIndex: number[] = [];
  //   for (let i = 0; i < data.length; i++) {
  //     if (data[i].temperatureBreach) {
  //       temperatureBreachesIndex.push(i);
  //     }
  //   }
  //   setTemperatureBreachesIndex(temperatureBreachesIndex);

  //   const setpointMode = computeMode(
  //     data
  //       .map((d) => d.temperature_setpoint)
  //       .filter((d) => d !== null) as number[],
  //   );
  //   setSetpointMode(setpointMode);
  // }, [data, powerOutagesFiltered, temperatureBreachesFiltered]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        chartContainerRef.current &&
        chartContainerRef.current.contains(event.target as Node)
      ) {
        console.log('zoomPoint', zoomPoint);
        mapRef.current?.flyTo({
          center: [zoomPoint?.current?.longitude, zoomPoint?.current?.latitude],
          zoom: 18,
        });
        console.log('Click was INSIDE the chart container div.');
      } else {
        console.log('Click was OUTSIDE the chart container div.');
      }
    };

    // Add event listener to the document
    document.addEventListener('mousedown', handleClickOutside);
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []); // Empty dependency array ensures this runs once after mount and cleans up on unmount

  const getTemperatureChartOption = () => ({
    animation: true,
    title: {
      text: t('reeferInsights.temperatureAndPower.temperature'),
      left: 'center',
      top: 10,
      textStyle: {
        fontSize: 16,
      },
    },
    tooltip: {
      trigger: 'axis',
      confine: true,
      formatter: function (params: any) {
        const timestamp = Array.isArray(params[0].value)
          ? params[0].value[0]
          : null;

        const ts = timestamp ? new Date(timestamp) : null;
        const index = params[0].dataIndex;
        const dataPoint = data[index]; // `data` from snap.linechart.data
        const formattedTs = ts
          ? new Date(ts).toISOString().replace('T', ' ').slice(0, 16) + ' UTC'
          : 'N/A';
        const leftAxisUnit = '°C';
        const rightAxisUnit = 'kW';

        // Check if this is a temperature breach point
        // Assumes dataPoint has a 'temperatureBreachId' field.
        // Uses 'temperatureBreachesFiltered' from snap.temperatureBreaches.filtered
        if (
          dataPoint &&
          (dataPoint.temperature_breach_id || dataPoint.power_outage_id)
        ) {
          const breach = temperatureBreachesFiltered.find(
            (b: any) => b.id === dataPoint.temperature_breach_id,
          );
          const powerOutage = powerOutagesFiltered.find(
            (b: any) => b.id === dataPoint.power_outage_id,
          );

          let duree = 'N/A';
          let deviationMax = 'N/A';
          let powerOutageDuration = 'N/A';

          if (breach) {
            // Calculate duration in hours and minutes
            // Assumes breach.start_timestamp and breach.end_timestamp are in seconds
            const dureeSeconds = breach.duration;
            const dureeMs = dureeSeconds;
            const heures = Math.floor(dureeMs / (60 * 60 * 1000));
            const minutes = Math.floor(
              (dureeMs % (60 * 60 * 1000)) / (60 * 1000),
            );
            duree = `${heures}h ${minutes}min`;

            // Get maximum deviation
            deviationMax = `${breach.max_deviation.toFixed(1)}${leftAxisUnit}`;
          }

          if (powerOutage) {
            // Calculate power outage duration
            const powerOutageSeconds = powerOutage.duration;
            const powerOutageMs = powerOutageSeconds;
            const heures = Math.floor(powerOutageMs / (60 * 60 * 1000));
            const minutes = Math.floor(
              (powerOutageMs % (60 * 60 * 1000)) / (60 * 1000),
            );
            powerOutageDuration = `${heures}h ${minutes}min`;
          }

          // Determine the title and color based on what's present
          let title = '';
          let backgroundColor = '';
          let borderColor = '';
          let iconColor = '';

          if (dataPoint.temperature_breach_id && dataPoint.power_outage_id) {
            // @ts-ignore
            title = dataPoint.hot_loading_id
              ? 'Hot Loading + Power Outage'
              : 'Temperature breach + Power Outage';
            backgroundColor = 'rgba(255, 107, 107, 0.1)';
            borderColor = '#FF6B6B';
            iconColor = '#FF6B6B';
          } else if (dataPoint.temperature_breach_id) {
            // @ts-ignore
            title = dataPoint.hot_loading_id
              ? 'Hot Loading'
              : 'Temperature breach';
            backgroundColor = 'rgba(255, 107, 107, 0.1)';
            borderColor = '#FF6B6B';
            iconColor = '#FF6B6B';
          } else if (dataPoint.power_outage_id) {
            title = 'Power Outage';
            backgroundColor = 'rgba(255, 107, 107, 0.1)';
            borderColor = '#FF6B6B';
            iconColor = '#FF6B6B';
          }

          return `
            <div style="padding: 8px; border-radius: 4px; background-color: ${backgroundColor}; border: 1px solid ${borderColor};">
              <div style="display: flex; align-items: center; margin-bottom: 5px;">
                <div style="font-weight: bold; color: ${iconColor}; margin-right: 5px;">${title}</div>
                <div style="width: 20px; height: 20px;">
                  <svg viewBox="0 0 24 24" fill="${iconColor}">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                </div>
              </div>
              <div style="font-weight: bold; margin-bottom: 8px;">
                ${breach ? `<div>${t('reeferInsights.chart.temperatureBreachDuration')}: ${duree}</div>` : ''}
                ${breach ? `<div>${t('reeferInsights.chart.maximumDeviation')}: ${deviationMax}</div>` : ''}
                ${powerOutage ? `<div>${t('reeferInsights.chart.powerOutageDuration')}: ${powerOutageDuration}</div>` : ''}
              </div>
              <hr style="border: 0; height: 1px; background-color: #FFD6D6; margin: 5px 0;" />
              <div>Datetime: ${formattedTs}</div>
              <div>${t('reeferInsights.chart.temperature')}: ${dataPoint.temperature_return}${leftAxisUnit}</div>
              <div>${t('reeferInsights.chart.setpoint')}: ${dataPoint.temperature_setpoint}${leftAxisUnit}</div>
              <div>${t('reeferInsights.chart.deviation')}: ${(dataPoint.temperature_return || 0 - (dataPoint.temperature_setpoint || 0)).toFixed(1)}${leftAxisUnit}</div>
              <div>${t('reeferInsights.chart.power')}: ${Math.round(dataPoint.active_power || 0)} ${rightAxisUnit}</div>
            </div>
          `;
        }

        // Normal display for other points
        let tooltipText = `<div style="font-weight: bold; margin-bottom: 5px;">Datetime: ${formattedTs}</div>`;

        let seriesDetails = '';
        params.forEach((param: any) => {
          if (param.seriesName === '_breachAreaBase') {
            // Hide helper series from tooltip
            return;
          }
          if (
            param.value &&
            param.value[1] !== null &&
            param.value[1] !== undefined
          ) {
            seriesDetails +=
              param.marker +
              param.seriesName +
              ': ' +
              param.value[1].toFixed(1) +
              (param.seriesName === t('reeferInsights.chart.power')
                ? rightAxisUnit
                : leftAxisUnit) + // Use leftAxisUnit for temp
              '<br/>';
          }
        });
        if (seriesDetails) {
          tooltipText += `<div style="margin-top: 5px;">${seriesDetails}</div>`;
        }

        return tooltipText;
      },
    },
    grid: {
      left: '5%',
      right: '5%',
      top: '15%',
      bottom: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'time',
      axisLabel: {
        formatter: (value: number) => dayjs(value).format('YYYY-MM-DD h:mm A'),
        rotate: 45,
      },
    },
    yAxis: [
      {
        name: `${t('reeferInsights.temperatureAndPower.temperature')} (°C)`,
        type: 'value',
        min: -30,
        max: 30,
        nameLocation: 'middle',
        nameGap: 30,
      },
      {
        name: `${t('reeferInsights.temperatureAndPower.power')} (kW)`,
        type: 'value',
        min: 0,
        position: 'right',
        nameLocation: 'middle',
        nameGap: 20,
        nameRotate: 90,
        splitLine: {
          show: false,
        },
      },
    ],
    series: [
      {
        name: t('reeferInsights.chart.ambient'),
        type: 'line',
        data: data.map((item, index) => ({
          value: [item.timestamp, item.temperature_ambient],
        })),
        color: '#4CAF50',
        connectNulls: false,
        emphasis: {
          focus: 'series',
          scale: 2.5,
        },
      },
      {
        name: t('reeferInsights.chart.return'),
        type: 'line',
        data: data.map((item, index) => ({
          value: [item.timestamp, item.temperature_return],
        })),
        color: '#0075B3',
        connectNulls: false,
        emphasis: {
          focus: 'series',
          scale: 2.5,
        },
      },
      {
        name: t('reeferInsights.chart.setpoint'),
        type: 'line',
        stack: 'x',
        data: data.map((item, index) => ({
          value: [item.timestamp, item.temperature_setpoint],
          // itemStyle: {
          //   opacity:
          //     powerOutagesIndex.includes(index) ||
          //     temperatureBreachesIndex.includes(index)
          //       ? 0
          //       : 1,
          // },
        })),
        color: '#FFA500',
        connectNulls: false,
        emphasis: {
          focus: 'series',
          scale: 2.5,
        },
      },
      {
        name: t('reeferInsights.chart.power'),
        type: 'line',
        yAxisIndex: 1,
        data: data.map((item, index) => ({
          value: [item.timestamp, item.active_power],
          itemStyle: {
            opacity: powerOutagesIndex.includes(index) ? 0 : 1,
          },
        })),
        color: '#FFD700',
        connectNulls: false,
        emphasis: {
          focus: 'series',
          scale: 2.5,
        },
      },
      {
        name: 'Power Outage',
        type: 'line',
        data: data.map((item) => [
          item.timestamp,
          item.power_outage_id ? 60 : null,
        ]),
        areaStyle: {
          color: '#FF6666',
          opacity: 0.3,
        },
        lineStyle: {
          opacity: 0,
        },
        stack: 'confidence-band',
        symbol: 'none',
        baseline: -30,
      },
      {
        name: 'Temperature Breach', // This will be the visible green area
        type: 'line',
        stackStrategy: 'all',
        stack: 'x', // Stacks on top of _breachAreaBase
        symbol: 'none',
        connectNulls: false,
        lineStyle: {
          opacity: 0, // No line, just area
        },
        areaStyle: {
          color: 'green',
          opacity: 0.7,
        },
        emphasis: {
          focus: 'series',
          areaStyle: {
            opacity: 0.9,
          },
        },
        data: data.map((item) => {
          if (
            item.temperature_breach_id &&
            item.temperature_return !== null &&
            item.temperature_setpoint !== null
          ) {
            return [
              item.timestamp,
              Math.abs(item.temperature_return - item.temperature_setpoint),
            ];
          }
          return [item.timestamp, null];
        }),
      },
    ],
  });

  const getGpsChartOption = () => ({
    title: {
      text: 'GPS',
      left: 'left',
      top: 10,
      textStyle: {
        fontSize: 16,
      },
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params: any) {
        const time = dayjs(params[0].value[0]).format('YYYY-MM-DD h:mm A');
        return `${time}<br/>
                Latitude: ${params[0].value[1].toFixed(4)}°<br/>
                Longitude: ${params[1].value[1].toFixed(4)}°`;
      },
    },
    grid: {
      left: '5%',
      right: '5%',
      top: '15%',
      bottom: '10%',
    },
    xAxis: {
      type: 'time',
      axisLabel: {
        formatter: (value: number) => dayjs(value).format('YYYY-MM-DD h:mm A'),
      },
    },
    yAxis: [
      {
        name: 'Coordinates',
        type: 'line',
        triggerLineEvent: true,
        axisLabel: {
          formatter: '{value}°',
        },
      },
    ],
    series: [
      {
        name: 'Latitude',
        type: 'line',
        data: gpsDataFiltered.map((item) => [item.timestamp, item.lat]),
        color: '#FFA500',
        symbol: null,
      },
      {
        name: 'Longitude',
        type: 'line',
        data: gpsDataFiltered.map((item) => [item.timestamp, item.lon]),
        color: '#0075B3',
        symbol: null,
      },
    ],
  });

  return (
    <>
      <Box
        ref={chartContainerRef}
        sx={{ position: 'relative', height: '100%' }}
      >
        <ReactECharts
          ref={ref}
          option={getTemperatureChartOption()}
          style={{ height: '100%' }}
          notMerge={true}
          lazyUpdate={true}
          onEvents={{
            highlight: (params: any) => {
              const batch = params.batch;
              const dataIndex = batch[0].dataIndex;
              // setHighlightedSensorDataFiltered(dataIndex);
              const sensorItem = data[dataIndex];

              const gpsPoint = gpsDataFiltered[dataIndex];
              zoomPoint.current = {
                latitude: gpsPoint.lat,
                longitude: gpsPoint.lon,
              };

              setHighlightedGpsDataFilteredIndex({
                timestamp: sensorItem.timestamp,
              });
            },
          }}
        />

        {showGps && (
          <ReactECharts
            option={getGpsChartOption()}
            style={{ height: '100%' }}
            notMerge={true}
            lazyUpdate={true}
            onEvents={{
              downplay: (params: any) => {
                console.log('downplay', params);
                if (!mapRef.current) return;
                const index = params.dataIndex;
                if (index !== undefined) {
                  const gpsSelected = gpsDataFiltered[index];
                  mapRef.current.flyTo({
                    center: [gpsSelected.lon, gpsSelected.lat],
                    zoom: 18,
                  });
                }
              },
              mouseover: (params: any) => {
                if (params.dataIndex !== undefined) {
                  setHighlightedGpsDataFilteredIndex({
                    index: params.dataIndex,
                  });
                }
              },
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">GPS</Typography>
        <IconButton onClick={toggleShowGps} size="small" sx={{ p: 0.5 }}>
          {showGps ? (
            <RemoveCircleOutlineIcon fontSize="small" />
          ) : (
            <AddCircleOutlineIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
    </>
  );
};
